/* eslint-disable no-use-before-define */
import {
  AVAILABLE_COMPONENTS,
  FIELD_TYPES,
} from 'components/AVAILABLE_COMPONENTS';
import type { PageSidebarSection } from 'components/sidebar/PageSidebar';
import {
  FIELD_NAMES,
  QUESTION_IDS,
  SKIP_REASON_CODES,
  FormFieldValidationStatus,
  DEPENDENCIES,
  VALIDATION_TYPES,
  CLICK_PORTAL_SEARCH_FILTERS,
  SITE_TYPE,
  CLICK_PORTAL_CARD_LAYOUT,
  CLICK_PORTAL_RESULT_ROWS_DISPLAY,
} from 'consts';
import { Experiment, Variant } from 'experiments/enums';
import { GetServerSidePropsContext, NextApiRequest } from 'next';
import { APIError } from 'utils/errors/APIError';

// ----------------------- //
// ------- Global -------- //
// ----------------------- //
export type LeadSubmitBatchId = string & { __id: 'LeadSubmitBatchId' };

export type SchoolImpressionGuid = string & { __id: 'SchoolImpressionGuid' };

export type DegreeTypeGuid = string & { __id: 'DegreeTypeGuid' };

export type ParentCategoryGuid = string & { __id: 'ParentCategoryGuid' };

export type CategoryGuid = string & { __id: 'CategoryGuid' };

export type ProgramGuid = string & { __id: 'ProgramGuid' };

export type ProfileGuid = string & { __id: 'ProfileGuid' };

export type LeadGuid = string & { __id: 'LeadGuid' };

export type SchoolCode = string & { __id: 'SchoolCode' };

export type ServiceGuid = string & { __id: 'ServiceGuid' };

declare global {
  interface Window {
    triadGrowthBookAPI?: GrowthBookAPI;
    isABTestingEnabled: boolean;
    tmsActiveExperiment?: [Experiment, Variant][];
    dataLayer?: { [key: string]: unknown }[];
    gtmConsentListeners?: ((purposes: Record<string, boolean>) => void)[];
    tmsSchoolCode?: string;
    tmsServiceGuid?: string;
  }
}

export interface PublicEnvironmentVariables {
  configEnv: 'production' | 'staging' | 'uat' | 'development' | 'dev1';
  buildId: string;
  revisionId: string;
  isRedisDisabled: boolean;
  jornayaCampaignId: string;
  availableThemes: Record<string, string>;
  WPHost: string;
  WPDomain: string;
  triadBackendDomain: string;
  abTestingClientKey: string;
  isDebugEnabled: boolean;
  developmentSiteUrl: string;
}

export interface TaxonomyValueType<T> {
  slug: string;
  id: string;
  guid: T;
  featuredPrograms: { guid: string }[];
  degreeFeaturedPrograms: {
    [__R in string]: {
      guid: string;
    }[];
  };
  title: string;
}

export interface TriadGetServerSidePropsContext
  extends GetServerSidePropsContext {
  req: GetServerSidePropsContext['req'] & {
    redisHelpers: RedisHelpers;
  };
}

export interface FooterLinkItem {
  link: string;
  label: string;
  trigger?: string;
}

export interface SidebarRange {
  startSectionKey?: string;
  endSectionKey?: string;
}

export interface ServerSidePageProps extends DomainLayoutWithEnv {
  pageSections?: [];
  sidebarSections?: [];
  sidebarRange?: SidebarRange;
  ogImg: string | undefined;
  title: string;
  taxonomyValues: GlobalContextType['taxonomyValues'];
  schoolListingResults: MultiRowSchoolListingResults;
}

export interface ArticleAttribution {
  author: string;
  avatar?: ImageObj;
}

/** Raw ACF image field before getImageDataFromWpResponse */
export interface WpImageResponse {
  url?: string;
  mime_type?: string;
  sizes?: Record<string, string>;
  srcset?: string;
  width?: number;
  height?: number;
  alt?: string;
  svg?: string;
}

/** Raw ACF attribution group on articles */
export interface WpArticleAttribution {
  author?: string;
  avatar?: WpImageResponse;
}

export interface ArticleTag {
  name: string;
  slug: string;
}

export interface ArticleType {
  title: string;
  slug: string;
  excerpt: string;
  date: string;
  href: string;
  tags?: ArticleTag[];
  featuredImage?: {
    image?: ImageObj;
    mobileImage?: ImageObj;
  };
  attribution: ArticleAttribution;
}

export interface ArticlePageProps extends DomainLayoutWithEnv {
  pageSections: unknown[];
  sidebarSections?: PageSidebarSection[];
  sidebarRange?: SidebarRange;
  featuredImage?: {
    image?: ImageObj;
    mobileImage?: ImageObj;
  };
  ogImg: string;
  title: string;
  excerpt: string;
  attribution: ArticleAttribution;
  tags: ArticleTag[];
  microSiteTaxonomyMap: NonNullable<GlobalContextType['microSiteTaxonomyMap']>;
  programTaxonomyMap: GlobalContextType['programTaxonomyMap'];
  deviceType: 'desktop' | 'mobile';
  taxonomyValues?: GlobalContextType['taxonomyValues'];
}

export interface ArticlesPageProps extends DomainLayoutWithEnv {
  className: string;
  title: string;
  pageSections: unknown[];
  sidebarSections?: PageSidebarSection[];
  sidebarRange?: SidebarRange;
  ogImg?: string | null;
  microSiteTaxonomyMap: NonNullable<GlobalContextType['microSiteTaxonomyMap']>;
  programTaxonomyMap: GlobalContextType['programTaxonomyMap'];
  deviceType: 'desktop' | 'mobile';
}

export interface WPDomainSettingsRawResponse {
  globalSiteSettings: {
    resultCardReviewTitle: string;
    footerText: string;
    footerLinks: {
      link: {
        title: string;
        url: string;
        target: string;
      };
    }[];
    footerComponent: string;
    footerSocialLinks?: {
      socialLinksTitle?: string;
      socialLinks?: {
        network: string;
        url: string;
        label: string;
      }[];
    };
    googleTagManagerSiteId: string;
    frontendDomain: string;
    robots: string;
    siteDescription: string;
    siteName: string;
    schoolCode: string;
    serviceGuid?: string;
    theme: string;
    disclaimerText: string;
    disclaimerSuccessText: string;
    sharedDisclaimerOptInText: string;
    disclosure: string;
    favicon: string;
    canonicalDomain: string;
    startDates: string;
    siteType: SITE_TYPE;
    isDraftMode: boolean;
    isABTestingEnabled: boolean;
    abTesting: {
      isEnabled: boolean;
    };
    admissionStartDates: string[];
    draftMode: boolean;
    questionnaires: Record<string, unknown>;
    ketch: {
      isEnabled: boolean;
      propertyId: string;
    };
    successText: string;
    successCta: {
      callToAction: {
        text: string;
        url: string;
        target: string;
      };
    };
    clickPortalSettings: {
      isMultiRow: boolean;
      showRelatedResults?: boolean;
      resultCardLayout?: CLICK_PORTAL_CARD_LAYOUT;
      schoolRatingTooltipText?: string;
      secondaryNavLabel: string;
      secondaryNavItems: {
        link: {
          title: string;
          url: string;
          target: string;
        };
      }[];
      resultCardReviewTitle: string;
      searchHeaderDescriptions?: {
        searchHeaderDescription?: string;
        searchHeaderDescriptionMobile?: string;
      };
    };
  };
  navigation: {
    mainNav: {
      label: string;
      url: string;
      target: string;
    }[];
  };
  headlines: {
    headline1: string;
    headline2: string;
  };
}

export interface DomainLayout {
  meta: {
    originSchoolCode?: string;
    seoTitle?: string;
    seoDescription?: string;
    seoKeywords?: string;
    domain: string;
    googleTagManagerSiteId: string;
    robots: string;
    siteDescription: string;
    siteName: string;
    schoolCode: string;
    serviceGuid: string;
    theme: string;
    canonicalDomain: string;
    ketch: {
      isEnabled: boolean;
      propertyId: string;
    };
    disclaimerSuccessText: string;
    disclosure: string;
    floodlightActivityValues: {
      eventValues: UserTrackingFilters[];
    };
    sharedDisclaimerOptInText: string;
    favicon: string;
    disclaimerText: string;
    siteType: SITE_TYPE;
    isDraftMode: boolean;
    isABTestingEnabled: boolean;
    clickPortalSettings: {
      isMultiRow: boolean;
      showRelatedResults: boolean;
      resultCardLayout: CLICK_PORTAL_CARD_LAYOUT;
      schoolRatingTooltipText?: string;
      secondaryNavItems: {
        label: string;
        url: string;
        target: string;
      }[];
      secondaryNavLabel: string;
      resultCardReviewTitle: string;
      searchHeaderDescription: {
        desktop: string;
        mobile: string;
      };
    };
  };
  formSettings: Record<string, unknown>;
  header: {
    headerComponent?: string;
    headertext?: string;
    useHeaderComponentOverride?: boolean;
    cta?: {
      useCTA: boolean;
      buttonText: string;
      buttonActionType: string;
      link: string;
    };
    useStickyHeader?: boolean;
    logoLink?: {
      url: string;
      title: string;
      target: string;
    };
    menuItems?: {
      label: string;
      url: string;
      target: string;
    }[];
  };
  footer: {
    footerText: string;
    footerLinks: {
      link: {
        title: string;
        url: string;
        target: string;
      };
    }[];
    footerComponent: string;
    socialLinks?: {
      network: string;
      url: string;
      label: string;
    }[];
    socialLinksTitle?: string;
  };
  headlines: Record<
    string,
    {
      title: string;
      subTitle: string;
      id: string;
      slug: string;
    }
  >;
}

export interface DomainLayoutWithEnv extends DomainLayout {
  env: PublicEnvironmentVariables;
  activeExperiment: [Experiment, Variant][] | null;
}

export interface GlobalContextType {
  activeExperiment: [Experiment, Variant][];
  env: PublicEnvironmentVariables;
  programTaxonomyMap: {
    degrees: TransformedTaxonomyNode[];
  } | null;
  microSiteTaxonomyMap: {
    degreeTypes: { [__T in DegreeTypeGuid]: TaxonomyValueType<__T> };
    parentCategories: { [__T in ParentCategoryGuid]: TaxonomyValueType<__T> };
    categories: { [__T in CategoryGuid]: TaxonomyValueType<__T> };
    programs: Program[];
  } | null;
  sessionInfo: {
    userSessionId: string | null;
    hasScrolled: boolean;
    pageViewId: string | null;
    floodLightActivityFilters: {
      trackingSchoolCode: string;
      propertyOrigin: string;
      campaignType: string;
      isClickUser: boolean;
      adPlatformSource: string;
    };
  };
  questionnaire: Questionnaire;
  questionnaires?: {
    default?: Questionnaire;
    clickPortal?: Questionnaire;
  };
  abTests: Record<string, unknown>;
  taxonomyValues: {
    degrees: DegreeTypeGuid[];
    parentCategories: ParentCategoryGuid[];
    categories: CategoryGuid[];
    programs: ProgramGuid[];
  };
  flags: {
    hasFormSidebar: boolean;
    hasStickyHeader: boolean;
    isFirstStep: boolean;
    isFormDirty: boolean;
    navIsOpen: boolean;
    isLoading: boolean;
    hasExpandedCard: boolean;
  };
  userData: {
    degreeSelection: {
      featuredPrograms: { guid: string }[];
      label: string;
      orderBy: number;
      value: DegreeTypeGuid;
    } | null;
    parentCategorySelection: {
      degreeFeaturedPrograms: {
        [__R in string]: {
          guid: string;
        }[];
      };
      featuredPrograms: { guid: string }[];
      label: string;
      orderBy: number;
      value: ParentCategoryGuid;
    } | null;
    categorySelection: CategoryGuid | null;
    programOfInterest: {
      parentCategory: ParentCategoryGuid[];
      title: string;
      guid: ProgramGuid;
      category: CategoryGuid[];
    } | null;
    applicationLink: string | null;
    linkedSessionFormValues: FormValues | null;
    submittedUserFormValues: FormValues | null;
  };
  refs: {
    heroRef: null;
    headerRef: null;
    modalContainerRef: React.RefObject<HTMLDivElement | null> | null;
    clickPortalSearchResultsRef: null | React.RefObject<HTMLDivElement | null>;
    themeWrapperRef: null;
  };
  resumeModal: {
    isOpen: boolean;
  };
  actions: {
    updateGlobalFlags: (flags: Record<string, unknown>) => null;
    setActiveModalId: (modalId: string) => null;
    updateUserData: (data: Record<string, unknown>) => null;
    updateMicroPortalData: (
      data: Partial<GlobalContextType['microPortal']>
    ) => null;
    updateGlobalRefs: (refs: Partial<GlobalContextType['refs']>) => null;
    updateClickPortalData: (
      data: Partial<GlobalContextType['clickPortal']>
    ) => null;
  };
  windowSize: {
    height: number | null;
    width: number | null;
    currentBreakpoint: string;
  };
  clickPortal: MultiRowSchoolListingResults & {
    error: string;
    isLoading: boolean;
    shouldShowSteps: boolean;
    currentSelection: Record<string, unknown>;
  };
  formSettings: DomainLayout['formSettings'];
  microPortal: {
    primaryFormSubmitPollingHelper: null;
    secondaryFormSubmitPollingHelper: null;
    additionalSchoolResultsPromise: null;
    leadSubmitResults: null;
    additionalSchoolResults: null;
    schoolListingResults: null;
    submittedSchools: School[];
    userFirstName: string;
  };
  siteMeta: DomainLayout['meta'];
}

export type TriadmsRequest = NextApiRequest & {
  redisHelpers: RedisHelpers;
};

export interface TriadmsResponse<T> {
  success: boolean;
  error: string;
  data: T;
}

export interface GetQuestionOptionsPayload {
  schoolCode: SchoolCode;
  serviceGuid: ServiceGuid;
  templateName: string;
  requestedOptions: QUESTION_IDS[];
  questionReplies: { questionId: QUESTION_IDS; questionAnswer: string }[];
  queryParams: { name: string; value: string }[];
  pageTaxonomyValues: GlobalContextType['taxonomyValues'];
}

export interface RedisHelpers {
  getQuestionBank: () => Promise<
    { [__T in QUESTION_IDS]?: Question } | undefined
  >;
  setQuestionBank: (questionBank: { [__T in QUESTION_IDS]?: Question }) => void;
  getQuestionnaire: (variant: string) => Promise<Questionnaire | undefined>;
  setQuestionnaire: (variant: string, questionnaire: Questionnaire) => void;
  getProgramTaxonomy: () => Promise<
    { degrees: TransformedTaxonomyNode[] } | undefined
  >;
  setProgramTaxonomy: (programTaxonomy: {
    degrees: TransformedTaxonomyNode[];
  }) => void;
  getDomainGlobalSettings: () => Promise<DomainLayoutWithEnv | undefined>;
  setDomainGlobalSettings: (globalSettings: DomainLayoutWithEnv) => void;
  getDomainQuestionBank: (
    request: GetQuestionOptionsPayload
  ) => Promise<{ [__T in QUESTION_IDS]?: Question } | undefined>;
  setDomainQuestionBank: (
    request: GetQuestionOptionsPayload,
    response: { [__T in QUESTION_IDS]?: Question }
  ) => void;
  getLandingPageLayoutTtl: (key: string) => Promise<number>;
  setLandingPageLayout: (key: string, layout: LandingPageLayout) => void;
  getLandingPageLayout: (key: string) => Promise<LandingPageLayout | undefined>;
  getMultiSchoolListings: (request: {
    body: unknown;
  }) => Promise<MultiRowSchoolListingResults | undefined>;
  setMultiSchoolListings: (
    request: { body: unknown },
    response: MultiRowSchoolListingResults
  ) => void;
}

export type FormFieldValue =
  | FormFieldValueObject<string | null | undefined>
  | FormFieldValueObject<SelectFieldOption[] | undefined>
  | FormFieldValueObject<string>[]
  | FormFieldValueObject<MultiSchoolSelectionState>
  | FormFieldValueObject<GooglePlace>
  | null
  | boolean
  | string
  | undefined;

export type FormValues = {
  [__T in FIELD_NAMES]?: FormFieldValue;
};

export interface FormFieldValueObject<T> {
  shouldSkip?: boolean | null;
  skipReason?: SKIP_REASON_CODES | null;
  label?: string | null;
  value: T;
  valueToSubmit?: string;
  guid?: string | null;
}

export type DynamicOptions = {
  [__T in QUESTION_IDS]?: {
    options: SelectFieldOption[] | School[] | GroupedOptions[];
    questionId: QUESTION_IDS;
  };
};

export interface FormStatus {
  stepsValidationStatus: StepValidationInfo[];
  isProcessing: boolean;
  isSubmitting: boolean;
  dynamicOptions: DynamicOptions;
  isInitializing: boolean;
  isDirty: boolean;
}

export interface Step {
  groupLabel?: string;
  heading: string;
  id: number;
  name: string;
  subTitle?: string;
  title?: string;
  questions: Question[];
  trueStepIndex?: number;
  progressMeterRange?: string;
  subStepIndex?: number;
}

export interface SelectFieldOption {
  value: string;
  label: string;
  selected: boolean;
  guid?: string;
}

export interface GroupedOptions {
  group: string;
  options: SelectFieldOption[];
}

export type SuperFetchReturn<T> =
  | [APIError, null, null]
  | [null, T, { isFromCache: boolean }];

export type OptionsArray = SelectFieldOption[] | GroupedOptions[];

export interface Question {
  name: FIELD_NAMES;
  label: string;
  skipValue?: string;
  isPii?: boolean;
  id: QUESTION_IDS;
  grouping: number;
  required: boolean;
  validations: {
    type: VALIDATION_TYPES;
    errorText: string;
  }[];
  questionId: QUESTION_IDS; // we need to remove this
  type: FIELD_TYPES;
  options?: OptionsArray;
  dependency?: {
    [__T in DEPENDENCIES]?: {
      type: __T;
      questionIds: QUESTION_IDS[];
      validAnswers: {
        [__R in QUESTION_IDS]?: QUESTION_IDS[];
      };
    };
  };
  additionalProperties: {
    mobile?: AdditionalProperties;
    tablet?: AdditionalProperties;
    desktop?: AdditionalProperties;
  };
  controlOverrides: {
    mobile?: FIELD_TYPES;
    tablet?: FIELD_TYPES;
    desktop?: FIELD_TYPES;
  };
}

export interface Questionnaire {
  schoolCode: string;
  variant: string;
  id: number;
  stepsCount: number;
  steps: Step[];
  useLandingPageHeadline?: boolean;
  isFullPage?: boolean;
  disableLogging?: boolean;
  initialStepIndex?: number;
  hasDynamicDisclaimer: boolean;
  customButtonText: {
    disclaimer: string;
    first: string;
    last: string;
  };
  disableTrustedForm?: boolean;
  mobileComponent?: AVAILABLE_COMPONENTS;
  component?: AVAILABLE_COMPONENTS;
  sidebarContent?: {
    content?: string;
    image?: {
      image: unknown;
      mobileImage: unknown;
    };
  };
  showSteps?: boolean;
}

export interface Program {
  value: string;
  label: string;
}

export interface Degree {
  value: string;
  label: string;
  programs: Program[];
}

export interface School {
  submissionType?: 'MicroportalSchoolCards' | 'MicroportalTCPAOnlySchool';
  orderBy?: number;
  degreeList: Degree[];
  description: string;
  featuredImage: { mobile: ImageObj; desktop: ImageObj };
  highlights: string;
  id: string;
  label: string;
  matchingProgramOptions: OptionsArray;
  originalSelectedProgram: Program;
  rating: number;
  schoolLogo: { mobile: ImageObj; desktop: ImageObj };
  terms: string;
  impressionGuid: SchoolImpressionGuid;
  impressionGroupGuid: string;
  hasTcpaCheckbox: boolean;
  submitText: string;
}

export interface BrowserOrServerNewRelicAgent {
  noticeError?: (error: string | Error, meta: ErrorMeta) => void;
  addCustomAttribute?: (key: string, value: string) => void;
  addPageAction?: (name: string) => void;
  log?: (name: string, attributes: Record<string, unknown>) => void;
  setErrorHandler?: (handler: (error: Error) => boolean) => void;
  setApplicationVersion?: (version: string) => void;
}

export interface UserTrackingFilters {
  mapKeys: {
    adPlatformDestination: string;
    isConversion: boolean | null;
    eventType: string;
  };
  filters: {
    adPlatformSource: string;
    campaignType: string;
    isClickUser: boolean | null;
    propertyOrigin: string;
    trackingSchoolCode: string;
  };
  google: {
    advertiserId: string;
    groupTagString: string;
    activityTagString: string;
  };
  microsoft: {
    category: string;
    action: string;
    label: string;
    value: number | string;
    uetTagId: string;
  };
}

// ----------------------- //
// -- Field Validations -- //
// ----------------------- //
export interface FieldValidationArguments<T> {
  value: T;
  name: string;
  errorMessage?: string;
  formValues?: FormValues;
}

export interface FieldValidationResult {
  [FIELD_NAMES: string]: string | null;
}

export type FieldValidationFunctionSignature<T> = (
  args: FieldValidationArguments<T>
) => FieldValidationResult | Promise<FieldValidationResult>;

export interface StepValidationInfo {
  status: FormFieldValidationStatus;
  fieldValidations: {
    [__T in FIELD_NAMES]?: FormFieldValidationStatus;
  };
}

// ----------------------- //
// ------- States -------- //
// ----------------------- //
export interface SchoolSelectionState {
  isExpanded: boolean;
  isAccepted: boolean;
  isSkipped: boolean;
  isShowingDetails: boolean;
  isSubmitted: boolean;
  selectedProgram: Program;
  schoolInfo: School;
}

export type MultiSchoolSelectionState = Record<string, SchoolSelectionState>;

export enum MultiSchoolSelectActionTypes {
  EXPAND_CARD = 'EXPAND_CARD',
  SKIP_CARD = 'SKIP_CARD',
  TOGGLE_SKIP = 'TOGGLE_SKIP',
  ACCEPT_TERMS = 'ACCEPT_TERMS',
  EXPAND_NEXT_CARD = 'EXPAND_NEXT_CARD',
  TOGGLE_DETAILS = 'TOGGLE_DETAILS',
  CLOSE_CARD = 'CLOSE_CARD',
  UPDATE_PROGRAM = 'UPDATE_PROGRAM',
  INITIALIZE = 'INITIALIZE',
  SYNC_CAROUSEL_INDEX = 'SYNC_CAROUSEL_INDEX',
}

export interface MultiSchoolSelectAction {
  type: MultiSchoolSelectActionTypes;
  payload: {
    id?: string;
    program?: Program;
    schools?: School[];
    handleFormFieldChange?: (
      updatedFieldValue: MultiSchoolSelectionState
    ) => void;
    phoneNumber?: string;
    secondaryPhoneNumber?: string;
    disclaimerText?: string;
    sharedDisclaimerOptInText?: string;
    index?: number;
  };
}

// ----------------------- //
// ------ Contexts ------- //
// ----------------------- //
export interface BasicFormWizardContext {
  isLastStep: boolean;
  allQuestionsInForm: Record<QUESTION_IDS, Question> | null;
  formValues: FormValues;
  formStatus: FormStatus;
  formValuesV2: {
    [__T in FIELD_NAMES]?: {
      status: {
        validationStatus: FormFieldValidationStatus;
      };
    };
  };
  fieldNameMap: { [__T in FIELD_NAMES]?: Question };
  formConfigs: Questionnaire;
  handleChange: (
    fieldValue: FormFieldValue,
    event: {
      name: FIELD_NAMES;
      shouldSkipFormChangeFlow?: boolean;
      isNonUserAction?: boolean;
    }
  ) => Promise<{
    isCurrentStepSkipable: boolean;
  }>;
  handleSubmit: () => void;
  currentQuestions: Question[];
  currentStepIndex: number;
  actions: {
    setStatus: (status: Partial<FormStatus>) => void;
  };
}

// ----------------------- //
// ---- API Interfaces ---- //
// ----------------------- //
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface QuestionRepliesStructureForBackend {}

export interface GetQuestionnaireArgs {
  domain: string;
  variant: string;
  schoolCode: string;
  questionBankMap: { [__T in QUESTION_IDS]?: Question };
  redisHelpers: RedisHelpers;
}

export interface GrowthBookAPI {
  isABTestingEnabled: boolean;
  // https://docs.growthbook.io/features/basics#default-values
  getFeatureValue: (feature: FEATURE_FLAGS) => string | number | JSON;
}

export enum FEATURE_FLAGS {
  ACTIVE_TESTING_VARIANT = 'active-testing-variant',
}

export interface UserTrackingFiltersResponse {
  tagDestination?: string;
  isConversion?: boolean | null;
  eventType?: string;
  adPlatformSource?: string;
  campaignType?: string;
  isClickUser?: boolean | null;
  propertyOrigin?: string;
  trackingSchoolCode?: string;
  gadAdvertiserId?: string;
  gadGroupTagString?: string;
  gadActivityTagString?: string;
  msaCategory?: string;
  msaAction?: string;
  msaLabel?: string;
  msaValue?: string | number;
  msaUetTagID?: string;
}

export interface APIMetaDataInfo {
  schoolCode: string;
  variant: string;
  disclaimerText: string;
}

export interface getQuestionOptionsPayload {
  questionReplies: {
    questionAnswer: string;
    questionId: string;
  }[];
}

export interface GetSchoolListingPayload {
  maxSchools?: number;
  leadEvalToken?: string;
  impressionGroup?: string;
  submittedImpressionGuids?: string[];
  pageTaxonomyValues: {
    degrees?: string;
    parentCategories?: string;
    categories?: string;
  };
  questionReplies: {
    questionAnswer: string;
    questionId: QUESTION_IDS;
  }[];
  requestedOptions: QUESTION_IDS[];
  requestType:
    | 'Primary'
    | 'Secondary'
    | 'MicroClickportal'
    | 'MultiClickPortal';
}

export interface GetQuestionOptionsParsedResponse {
  [key: string]: {
    questionId: QUESTION_IDS;
    options: SelectFieldOption[] | School[];
  };
}

export interface SchoolListingSelectedProgram {
  DegreeTypeGuid: string;
  categoryPriority: number;
  destinationUrl: string;
  label: string;
  matchesFilterTag: boolean;
  shortDesc: string;
  value: string;
}

export interface SchoolDate {
  schoolDateType?: string;
  label: string;
  degreeTypeGuids?: string[];
  programGuids?: string[];
  dates?: string[];
}

export interface SchoolOptionAPIResponse {
  submissionType?: 'MicroportalSchoolCards' | 'MicroportalTCPAOnlySchool';
  orderBy?: number;
  HasFeaturedPrograms: boolean;
  IsOffsiteConversion: boolean;
  MatchingCategory: string;
  destinationUrl: string;
  filteredProgramCount: number;
  featuredImage?:
    | {
        Desktop?: {
          imageUrl: string;
          srcset?: string;
          height?: number;
          width?: number;
        };
        Mobile?: {
          imageUrl: string;
          srcset?: string;
          height?: number;
          width?: number;
        };
      }
    | undefined;
  highlights: string;
  schoolDates?: SchoolDate[];
  impressionGuid: SchoolImpressionGuid;
  label: string;
  learningEnvironment: string;
  programCount: number;
  programs?: {
    ProgramList?: {
      DegreeTypeGuid: string;
      categoryPriority: number;
      destinationUrl: string;
      label: string;
      matchesFilterTag: boolean;
      shortDesc: string;
      value: string;
    }[];
    value: string;
    label: string;
  }[];
  rating: number;
  schoolCode: string;
  schoolDesc?: string[];
  schoolImages?:
    | {
        Desktop?: {
          imageUrl: string;
          srcset?: string;
          height?: number;
          width?: number;
        };
        Mobile?: {
          imageUrl: string;
          srcset?: string;
          height?: number;
          width?: number;
        };
      }
    | undefined;
  selectedProgram?: SchoolListingSelectedProgram;
  value: string;
  disclaimer: string;
  Revenue?: number;
  FilterTagName?: string;
  matchingCategory?: string;
  optIn: boolean;
  submitText: string;
}

export interface LeadSubmitRawResponse {
  status: string;
  result: {
    ProfileId: ProfileGuid;
    LeadGuid: LeadGuid;
    Message: string;
    Revenue: number;
    AdjustedRevenue: number;
    Links: { ApplicationLink: string } | undefined;
  } | null;
}

export interface LeadSubmitPollingRawResponse {
  schoolLeadResponses: {
    leadStatus: string;
  }[];
  summary: {
    isFirePixel: boolean;
    profileId: ProfileGuid;
    totalLeadRev: number;
    totalAdjLeadRev: number;
  };
}

export interface GetQuestionOptionsRawResponse {
  questions: { questionId: string; options: SelectFieldOption[] }[];
}

export interface GetMultiRowSchoolListingRawResponse {
  results: GetSchoolListingRawResponse[];
}

export interface GetSchoolListingRawResponse {
  Listings: SchoolOptionAPIResponse[];
  impressionGroup: string;
  SchoolImpressionGroupGuid: string;
  IsValid?: boolean;
  cacheKey?: string;
  Errors?: string[];
  ImpressionKey?: string;
  Headline1?: string;
  Headline2?: string;
  ParentCategory?: string;
  DegreeType?: string;
  Category?: string;
  HasTags?: boolean;
}

export interface LeadSubmitResponse {
  shouldTrackConversion: boolean;
  profileGuid: ProfileGuid;
  leadsSubmittedFor: Array<{
    leadGuid: string;
    statusMessage: string;
    status: string;
    revenue: number;
    adjustedRevenue: number;
    schoolCode: string;
    links: { applicationLink: string } | undefined;
  }>;
}

export interface MicroPortalLeadSubmitResults {
  shouldTrackConversion: boolean;
  summary: {
    revenue: number;
    adjustedRevenue: number;
  };
  isWaitMoreResults: boolean;
  profileGuid: ProfileGuid;
  leadsSubmittedFor: {
    status: string;
  }[];
}

// https://developers.google.com/maps/documentation/geocoding/requests-geocoding#Types
export type GooglePlaceAddressComponentType =
  | 'street_number'
  | 'route'
  | 'locality'
  | 'political'
  | 'administrative_area_level_2'
  | 'administrative_area_level_1'
  | 'country'
  | 'postal_code'
  | 'neighborhood';

export interface GooglePlaceAddressComponent {
  longText: string;
  shortText: string;
  types: GooglePlaceAddressComponentType[];
  languageCode: string;
}

export interface GooglePlace {
  formattedAddress: string;
  placeId?: string;
  staticMapUrl?: string;
  addressComponents: GooglePlaceAddressComponent[];
  location: {
    latitude: number;
    longitude: number;
  };
}

export interface GoogleAutoCompleteResult {
  placePrediction: {
    placeId: string;
    text: {
      text: string;
    };
  };
}

export interface GooglePlacesApiResponse {
  places?: GooglePlace[];
}

export interface GooglePlacesAutoCompleteResponse {
  suggestions: GoogleAutoCompleteResult[];
}

export interface TriadGooglePlaceApiResponse<T> {
  data: T;
  error: string;
  success: boolean;
}

type ScreenWidthType = 'mobile' | 'tablet' | 'desktop';

interface PositioningConfig {
  mobile?: string;
  tablet?: string;
  desktop?: string;
}

interface AdditionalPropertyBase {
  screenWidth: ScreenWidthType[];
}

interface HelpTextProperty extends AdditionalPropertyBase {
  text: string;
  isVisible: boolean;
}

interface ToolTipProperty extends AdditionalPropertyBase {
  text: string;
  label: string;
  positioning: PositioningConfig;
}

interface PlaceholderTextProperty extends AdditionalPropertyBase {
  text: string;
}

interface AnimatedLabelProperty extends AdditionalPropertyBase {
  text: string;
}

interface HiddenLabelProperty extends AdditionalPropertyBase {
  hiddenLabelToggle: boolean;
}

interface WidthProperty extends AdditionalPropertyBase {
  width: string;
}

interface ShouldNotSkipProperty extends AdditionalPropertyBase {
  shouldNotSkip: boolean;
}

export interface AdditionalProperties {
  helpText?: HelpTextProperty;
  toolTip?: ToolTipProperty;
  placeHolderText?: PlaceholderTextProperty;
  animatedLabel?: AnimatedLabelProperty;
  hiddenLabel?: HiddenLabelProperty;
  width?: WidthProperty;
  shouldNotSkip?: ShouldNotSkipProperty;
}

export interface WordpressQuestionnaire {
  questionnaireId: number;
  questionnaireSlug: string;
  questionnaireName: string;
  isDefault?: boolean;
  successRedirectUrl?: string;
  title?: string;
  subTitle?: string;
  customButtonText?: {
    first?: string;
    last?: string;
    disclaimer?: string;
  };
  groupAttributes?: Record<number, { groupLabel: string }>;
  steps: {
    heading: string;
    title: string;
    subTitle: string;
    progressMeter: string;
    stepId: number;
    questions: {
      id: number;
      questionId: number;
      showCityState: boolean;
      grouping: number;
      additionalProperties?: AdditionalProperties;
      controlOverrides: {
        mobile?: FIELD_TYPES;
        tablet?: FIELD_TYPES;
        desktop?: FIELD_TYPES;
      };
      dependencyOverrides: {
        type: DEPENDENCIES;
        questionIds: QUESTION_IDS[];
        validAnswers: {
          [__R in QUESTION_IDS]?: QUESTION_IDS[];
        };
      };
      label: string;
    }[];
  }[];
  mobileComponent?: string;
  component?: string;
  displayOptions?: {
    showSidebar: boolean;
    showSteps: boolean;
  };
}

// ----------------------- //
// ------ Props ------- //
// ----------------------- //
export interface FormFieldProps<T = SelectFieldOption> {
  name: FIELD_NAMES;
  label?: string;
  error?: string;
  onChange: BasicFormWizardContext['handleChange'];
  value: FormFieldValue;
  onFocus: () => void;
  onBlur: () => void;
  options?: T[];
  required: boolean;
}

// ----------------------- //
// -------- Hooks -------- //
// ----------------------- //
export interface UseMultiSchoolSelect {
  state: MultiSchoolSelectionState;
  handleSelectSchool: (id: string) => void;
  handleSkipSchool: (id: string) => void;
  handleToggleSkip: (id: string) => void;
  handleAcceptTerms: (
    id: string,
    questionReplies: QuestionRepliesStructureForBackend,
    metaData: APIMetaDataInfo
  ) => void;
  handleNextClick: () => void;
  handleToggleDetails: (id: string) => void;
  handleCloseCard: (id: string) => void;
  handleProgramChange: (id: string, program: Program) => void;
  handleSyncCarouselIndex: (index: number) => void;
}

export interface UseMicroPortalFormHandler {
  onMoveToNextStep: (formContext: BasicFormWizardContext) => void;
  onOptionsRequest: (
    payload: GetSchoolListingPayload,
    schoolCode: string,
    variant: string
  ) => Promise<GetQuestionOptionsParsedResponse>;
  onFormSubmit: () => Promise<MicroPortalLeadSubmitResults>;
  onLogProgress: (
    formValues: BasicFormWizardContext['formValues'],
    fieldNameMap: BasicFormWizardContext['fieldNameMap'],
    formConfigs: BasicFormWizardContext['formConfigs'],
    linkedSessionFormValues: BasicFormWizardContext['formValues'],
    lastQuestionAnswered: Question
  ) => Promise<void>;
}

// ----------------------- //
// -- Program Taxonomy -- //
// ----------------------- //
export interface Category {
  orderBy: number;
  value: string;
  label: string;
}

export interface ParentCategory {
  orderBy: number;
  categories: Category[];
  value: string;
  label: string;
}

export interface DegreeType {
  parentCategories: ParentCategory[];
  value: string;
  label: string;
}

export interface TransformedTaxonomyNode {
  value: string;
  label: string;
  level: number;
  children: TransformedTaxonomyNode[];
  orderBy?: number;
}

export interface SchoolResult {
  id: string;
  name: string;
  schoolLogoUrl: {
    mobile: ImageObj;
    desktop: ImageObj;
  };
  featuredImage: {
    mobile: ImageObj;
    desktop: ImageObj;
  };
  description: string;
  schoolDesc?: string[];
  rating: number | null;
  url: string | null;
  learningType: string | null;
  isOffsiteConversion: boolean;
  highlights: string[];
  schoolDates?: SchoolDate[];
  selectedProgram: Partial<SchoolListingSelectedProgram>;
  otherPrograms: Record<string, unknown>[];
  filteredProgramCount: number | null;
  programCount: number | null;
  matchingCategory: string | null;
  schoolCode: string | null;
  revenue: number;
  impressionGuid: string | null | undefined;
  filterTagName: string;
}
export interface ImageObj {
  url: string;
  mimeType?: string;
  sizes?: {
    small?: {
      url: string;
      width: number;
      height: number;
    };
    medium?: {
      url: string;
      width: number;
      height: number;
    };
    large?: {
      url: string;
      width: number;
      height: number;
    };
  };
  srcSet?: string;
  width?: number;
  height?: number;
  alt?: string;
  svg?: string;
}

export interface GetSchoolListingResults {
  hasImpressionGuids: boolean;
  title: string;
  subTitle: string;
  impressionCacheKey?: string;
  schoolImpressionGroupGuid: string;
  results: {
    id: string;
    name: string;
    schoolLogoUrl: {
      mobile: ImageObj;
      desktop: ImageObj;
    };
    featuredImage: {
      mobile: ImageObj;
      desktop: ImageObj;
    };
    description: string;
    rating: number | null;
    url: string | null;
    learningType: string | null;
    isOffsiteConversion: boolean;
    highlights: string[];
    schoolDates?: SchoolDate[];
    selectedProgram: Partial<SchoolListingSelectedProgram>;
    otherPrograms: Record<string, unknown>[];
    filteredProgramCount: number | null;
    programCount: number | null;
    matchingCategory: string | null;
    schoolCode: string | null;
    revenue: number;
    impressionGuid: string | null | undefined;
    filterTagName: string;
  }[];
  hasTags: boolean;
  filterKeyDerivedValues: {
    hasDerivedValues: boolean;
    parent_category_guid: string | null;
    degree_level_guid: string | null;
    category_guid: string | null;
  };
}

export interface MultiRowSchoolListingResults {
  resultRows: GetSchoolListingResults[];
  isMultiRow: boolean;
  firstRow: GetSchoolListingResults;
}

export type SchoolFilters = Record<
  CLICK_PORTAL_SEARCH_FILTERS,
  { value: string | undefined } | string | undefined
>;

export interface ExperimentConfig {
  [schoolCode: string]: {
    isSiteExperimentsEnabled: boolean;
    experiments: Experiment[];
  };
}

export interface CtaProps {
  useCTA?: boolean;
  buttonText?: string;
  link?: string;
  buttonActionType?:
    | 'clickPortalWizard'
    | 'fullPageForm'
    | 'applicationLink'
    | 'customLink';
}

export interface ErrorMeta {
  priority: ErrorPriority;
  description?: string;
  endpoint?: string;
  schoolCode?: string;
  statusCode?: number;
  method?: string;
  route?: string;
}

export enum ErrorPriority {
  /**
   * P1 (Critical) - Immediate action required
   * Lead submission failures (revenue-impacting)
   * Complete site outages or critical pages down
   * Data corruption or loss
   * Security vulnerabilities
   * Payment processing failures (if applicable)
   */
  ONE = 1,
  /**
   * P2 (High) - Action required soon
   * Tracking/analytics failures (business intelligence impact)
   * Major features broken but workarounds exist
   * Significant performance degradation
   * Form validation issues that block some users
   */
  TWO = 2,
  /**
   * P3 (Medium) - Should be addressed
   * Non-critical frontend errors (visual issues, minor UX problems)
   * Errors affecting non-essential features
   * Issues with limited user impact
   * Minor performance issues
   */
  THREE = 3,
  /**
   * P4 (Low) - Nice to fix
   * Cosmetic issues
   * Development/staging-only errors
   * Non-blocking warnings
   * Edge case errors with minimal impact
   */
  FOUR = 4,
}

export interface LandingPageLayout {
  title: string;
  description: string;
  seoMeta: DomainLayout['meta'];
  formSettings: DomainLayout['formSettings'];
  sidebarSections?: PageSidebarSection[];
  sidebarRange?: SidebarRange;
  sections: unknown[];
  header: DomainLayout['header'];
  showNavigation: boolean;
  taxonomy: GlobalContextType['taxonomyValues'];
  ogImg: string;
}

/** Layout returned by getArticlePage / getPageLayout for contentType `articles` */
export interface ArticlePageLayout {
  title: string;
  excerpt: string;
  seoMeta?: DomainLayout['meta'];
  sections: unknown[];
  sidebarSections?: PageSidebarSection[];
  sidebarRange?: SidebarRange;
  header: DomainLayout['header'];
  ogImg: string;
  featuredImage?: {
    image?: ImageObj;
    mobileImage?: ImageObj;
  };
  /** Raw WP ACF attribution; normalize with getArticleAttributionFromWpResponse */
  attribution?: WpArticleAttribution;
  tags: ArticleTag[];
}

export interface Geolocation {
  cfCity: string;
  cfCountry: string;
  cfContinent: string;
  cfLongitude: string;
  cfLatitude: string;
  cfRegion: string;
  cfRegionCode: string;
  cfMetroCode: string;
  cfPostalCode: string;
  cfTimezone: string;
}

export interface Filter {
  degreeType: string | null;
  parentCategory: string | null;
  category: string | null;
}

export interface ResultRow {
  title: string;
  subTitle: string;
  resultType: 'filters' | 'filterKey';
  filterKey: string;
  filters: Filter;
}

export interface ClickPortalNavItem {
  label: string;
  url: string;
  target: string;
}

export interface ClickPortalResultRowsSectionProps {
  background: string;
  variation: string;
  description: string;
  customCssClass: string;
  isReversed: string;
  textAlignment: string;
  title: string;
  mobileTitle: string;
  subTitle: string;
  isCurated: boolean;
  showTitleAndDescription?: boolean;
  showSearchHeader?: boolean;
  showSearchControls?: boolean;
  searchControlsPosition?: 'sidebar' | 'inline' | string;
  listingDisplay?: CLICK_PORTAL_RESULT_ROWS_DISPLAY | string;
  resultCardLayout?: CLICK_PORTAL_CARD_LAYOUT | string;
  navLabel?: string;
  navItems?: ClickPortalNavItem[];
  resultRowsConfig: ResultRow[];
  initalResults: MultiRowSchoolListingResults | null;
}
